import { useEffect, useState, type ReactNode } from 'react'
import { Button, Icon, toastError } from 'admination-react'
import { useApp } from '@/core/state/appStore'
import { blattKonflikt, blattPdf, documentBlob } from '@/core/api/documents'
import { ApiError } from '@/core/api/http'
import { getParticipant } from '@/core/data/coachings'
import { currentVariant } from '@/core/data/session'
import { descriptionFor, gelistetesDokument } from '@/variants'
import { initI18n } from '@/core/i18n'
import { getStoredDoc, getStoredDocById, hasStoredFile, type StoredDoc } from '@/core/data/dokumente'
import { docFilePrefix, docLabel, docTitle } from '@/core/lib/docLabels'
import { downloadBlob, extForMime, personDownloadName } from '@/core/lib/files'
import { ModalHeader } from '../ModalHeader'
import { PdfRegion } from '../PdfRegion'

type Geladen = { doc: StoredDoc | null; blob: Blob | null; leer?: 'offen' | 'eingeschraenkt' | 'ohneZeile' }

async function sheetGeladen(doc: StoredDoc): Promise<Geladen> {
  try {
    return { doc, blob: await blattPdf(doc.id) }
  } catch (e) {
    const konflikt = blattKonflikt(e)
    if (konflikt === 'nicht_ausgefuellt') return { doc, blob: null, leer: 'offen' }
    if (konflikt === 'eingeschraenkt' || (e instanceof ApiError && e.status === 404))
      return { doc, blob: null, leer: 'eingeschraenkt' }
    throw e
  }
}

export function GespeichertesDokument({ coachingId }: { coachingId: string | null }) {
  const { state, dispatch, coachings, zurueckgezogen } = useApp()

  const name = state.screen?.document.name ?? null
  const docId = name ? state.screen?.document.id ?? null : null
  const [geladen, setGeladen] = useState<Geladen | null>(null)

  const [version, setVersion] = useState(0)

  const [bearbeiten, setBearbeiten] = useState(false)
  const [busy, setBusy] = useState(false)
  const row = coachings.find((c) => c.id === coachingId) ?? zurueckgezogen.find((c) => c.id === coachingId)
  const listedSheet = !!name && !!row?.sheets.includes(name)
  const eingeschraenkt =
    geladen?.leer === 'eingeschraenkt' || (geladen?.leer === 'ohneZeile' && listedSheet)
  const title = !name ? '' : geladen?.doc ? docTitle({ name, ...geladen.doc }) : docLabel(name)

  useEffect(() => {
    setGeladen(null)
    setBearbeiten(false)
    if (!coachingId || !name) return
    let cancelled = false
    const load = async (): Promise<Geladen> => {
      const doc = docId ? await getStoredDocById(docId) : await getStoredDoc(coachingId, name)
      if (!doc) return { doc, blob: null, leer: 'ohneZeile' }
      if (!hasStoredFile(doc)) return { doc, blob: null }
      return doc.sheet ? sheetGeladen(doc) : { doc, blob: await documentBlob(doc.id) }
    }
    load().then(
      (result) => {
        if (!cancelled) setGeladen(result)
      },
      (e: unknown) => {
        if (cancelled) return
        console.error('Dokument laden fehlgeschlagen:', e)
        toastError(`${docLabel(name)} konnte nicht geladen werden.`)
        setGeladen({ doc: null, blob: null })
      },
    )
    return () => {
      cancelled = true
    }
  }, [coachingId, name, docId, version])

  useEffect(() => {
    if (eingeschraenkt) toastError(initI18n().t('akte.openRestricted', { ns: 'vergabeStaff' }))
  }, [eingeschraenkt])

  async function download() {
    if (!geladen?.doc || !geladen.blob || !name) return
    const ext = extForMime(geladen.doc.fileType || geladen.blob.type)
    const prefix = docFilePrefix({ name, sheet: geladen.doc.sheet })
    let filename = `${prefix}.${ext}`

    try {
      const participant = coachingId ? await getParticipant(coachingId) : null
      filename = personDownloadName(prefix, participant?.nachname, participant?.vorname, filename, ext)
    } catch (e) {
      console.error('Dateiname für Download fehlgeschlagen:', e)
    }
    downloadBlob(geladen.blob, filename)
  }

  const ohneDatei = geladen?.doc != null && !hasStoredFile(geladen.doc)

  const eintrag =
    geladen && name && !row?.zurueckgezogen && !geladen.doc?.ersetzt
      ? gelistetesDokument(descriptionFor(currentVariant()).gelisteteDokumente, name)
      : undefined
  const bearbeitbar = !!eintrag?.bearbeitbar && !!geladen?.blob
  const Nachreichen =
    eintrag && (bearbeiten ? bearbeitbar : !geladen?.blob && (geladen?.leer === 'ohneZeile' || ohneDatei)) ? eintrag.Nachreichen : null

  const gespeichert = () => {
    setBearbeiten(false)
    if (docId) dispatch({ type: 'SELECT_COACHING_DOC', name })
    else setVersion((v) => v + 1)
  }

  const renderHeader = (actions?: ReactNode) => (
    <ModalHeader title={title} onClose={() => dispatch({ type: 'DISMISS' })} closeDisabled={busy} closeTestId="dokument-schliessen">
      <div className="pill-cluster">
        {actions}
        {bearbeitbar && !bearbeiten && (
          <div className="pill">
            <Button iconOnly title="Bearbeiten" data-testid="dokument-bearbeiten" onClick={() => setBearbeiten(true)}>
              <Icon name="i-edit" />
            </Button>
          </div>
        )}
        <div className="pill">
          <Button
            iconOnly
            title="Herunterladen"
            data-testid="dokument-herunterladen"
            disabled={!geladen?.blob} onClick={() => void download()}>
            <Icon name="i-download" />
          </Button>
        </div>

      </div>
    </ModalHeader>
  )

  return (
    <>
      {Nachreichen && coachingId && name ? (
        <Nachreichen coachingId={coachingId} name={name} onGespeichert={gespeichert} onBusyChange={setBusy} renderHeader={renderHeader} />
      ) : (
        <>
          {renderHeader()}
          <PdfRegion
            testId="dokument-ansicht"

            path={null}
            localBlob={geladen?.blob ?? null}
            waiting={geladen === null}
            loadingLabel={`${title} wird geladen …`}
            placeholder={
              geladen?.leer === 'offen'
                ? 'Dieses Blatt wurde noch nicht ausgefüllt.'
                : ohneDatei
                  ? `Für ${title} ist keine Datei hinterlegt.`
                  : `${title} konnte nicht geladen werden.`
            }
          />
        </>
      )}
    </>
  )
}
